import { dots, drop, figure, groundRing, moon, slash, speed, sword } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * กระบี่ฟังเงา (dim-moon, yin): a slim jian with a white mourning tassel, a pale moon, mist and tears. Re-themed to
 * quiet / mourning (10-13): no blood, no poison green; what lingers in a wound is a pale sorrow wisp.
 */

const MOON = '#e8eef8';
const SILVER = '#b8c4dc';
const MIST = '#8fa0c8';
const CLOTH = '#f4f1ea';
const TEAR = '#bfe0ff';
const DUSK = '#4a4668';

/** The slim jian with its white tassel. */
const jian = (p: Pix, x0: number, y0: number, x1: number, y1: number) =>
  sword(p, x0, y0, x1, y1, { hilt: SILVER, blade: MOON, tassel: CLOTH, w: 2 });

/** A sorrow wisp: a pale curl of mist rising. */
function wisp(p: Pix, x: number, y: number): void {
  p.path(
    [
      [x, y],
      [x + 2, y - 2],
      [x + 1, y - 4],
      [x + 3, y - 6],
    ],
    MIST,
    1,
  );
  p.px(x + 3, y - 7, MOON);
}

registerIcons('skill', {
  // Moonlight strokes along the blade, a sorrow wisp left in the cut.
  'ms-moon': (p) => {
    moon(p, 9, 9, 6, MOON);
    slash(p, 16, 18, 11, -0.6, 1.6, SILVER, 2);
    jian(p, 6, 28, 27, 7);
    wisp(p, 23, 26);
  },
  // A veil of moonlight hiding the blade: sheer hanging strips, the point cutting through.
  'ms-veil': (p) => {
    for (let k = 0; k < 5; k++) {
      const x = 5 + k * 5;
      for (let y = 3 + (k % 2); y < 27; y += 2) p.px(x, y, SILVER).px(x + 1, y + 1, MIST);
    }
    p.rect(3, 2, 26, 2, CLOTH);
    jian(p, 3, 29, 26, 10);
  },
  // Longing: three thrusts reaching for a far moon.
  'ms-longing': (p) => {
    moon(p, 25, 7, 5, MOON);
    for (let k = 0; k < 3; k++) dots(p, 3, 14 + k * 5, 20, 9 + k * 3, k === 1 ? MOON : MIST, 2);
    jian(p, 2, 27, 20, 14);
    drop(p, 25, 18, 1.6, TEAR);
  },
  // Stepping through as a moon shadow: a fading line of after-bodies behind the blade.
  'ms-step': (p) => {
    figure(p, 3, 14, DUSK, 'run');
    figure(p, 10, 13, MIST, 'run');
    figure(p, 18, 12, MOON, 'run');
    speed(
      p,
      18,
      [
        [24, 14],
        [27, 9],
      ],
      SILVER,
    );
    slash(p, 22, 16, 8, -1.2, 1.0, SILVER, 2);
  },
  // Night wraps round: a dark ring of mist, a crescent overhead, ash falling.
  'ms-night': (p) => {
    p.ell(16, 22, 13, 6, DUSK);
    groundRing(p, 16, 22, 13, 6, MIST);
    moon(p, 16, 9, 6, MOON);
    for (const [x, y] of [
      [6, 6],
      [26, 11],
      [9, 16],
      [23, 4],
    ] as const)
      p.px(x, y, SILVER);
  },
  // The night's kiss: the point at a closed eye, a tear drawn up the blade.
  'ms-kiss': (p) => {
    p.ring(19, 11, 7, 3, SILVER, 0.2, Math.PI - 0.2);
    for (const x of [14, 17, 20, 23]) p.px(x, 15, SILVER);
    drop(p, 18, 20, 2, TEAR);
    jian(p, 3, 29, 15, 19);
    p.px(11, 23, TEAR).px(8, 25, TEAR);
  },
  // The moon in the water: a crescent and its broken reflection.
  'ms-mirror': (p) => {
    moon(p, 16, 9, 7, MOON);
    p.rect(3, 17, 26, 1, MIST);
    for (let y = 19; y < 29; y += 2) {
      const w = 12 - (y - 19);
      p.rect(16 - w / 2 + ((y / 2) % 2), y, w, 1, y % 4 === 1 ? MOON : SILVER);
    }
  },
  // The eclipse: a dark moon with a thin bright rim, six cuts round it.
  'ms-eclipse': (p) => {
    p.ell(16, 16, 9, 9, MOON);
    p.ell(17, 15, 8, 8, '#1e1a30');
    for (let k = 0; k < 6; k++) {
      const a = (k * Math.PI) / 3 + 0.3;
      const x = 16 + Math.cos(a) * 13;
      const y = 16 + Math.sin(a) * 13;
      p.line(x - Math.sin(a) * 3, y + Math.cos(a) * 3, x + Math.sin(a) * 3, y - Math.cos(a) * 3, C.white);
    }
  },
});
